import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import { lazy, Suspense, useCallback, useEffect, useState } from 'react';

import { usePostOfficeContext } from '@atlassian/post-office-context/post-office-context';

import { usePostOfficeEnvironment } from '@atlassian/post-office-context/post-office-environment';

import TwcXflowModal from '@atlassian/twc-xflow-modal/main';

import { SOURCE_COMPONENT, SOURCE_CONTEXT } from '@atlassian/twc-xflow-modal/constants';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--townsquare/townsquare-screen-space-flags';

import { type MessageTemplateContext } from '../../..';

import { toSourceContext } from '../../../utils/to-source-context';

import { cleanupLaunchParams } from '../../../utils/cleanup-launch-params';

import { toSourceComponent } from '../../../utils/to-source-component';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

type HydrationResult = MessageTemplateContext & {
	locale: string;
};

type RenderProps = HydrationResult & PlacementRenderProps;

const TWC_HOME_ONBOARDING_EXPERIENCE_GATE = 'twcg-2256-home-twc-onboarding-experience';

const LazyTwcOnboardingExperience = lazy(() =>
	import('@atlassian/twc-onboarding-launcher/twc-onboarding-experience').then(
		({ TwcOnboardingExperience }) => ({
			default: TwcOnboardingExperience,
		}),
	),
);

export const render: {
	type: 'render';
	validate: (input: unknown) => RenderProps;
	Component: (
		input: PlacementRenderProps & {
			cloudId: string;
			context?: string;
			scopes?: Array<{
				name: string;
			}>;
			isSiteAdmin?: boolean;
			source?: string;
		} & {
			locale: string;
		},
	) => JSX.Element | null;
} = PlacementFrontend.createFrontendStage({
	type: 'render',
	validate: noValidation<RenderProps>(),
	Component: ({ cloudId, locale = 'en', source, context, isSiteAdmin = true }) => {
		const { orgId } = usePostOfficeContext();
		const { currentEnv } = usePostOfficeEnvironment();
		const [isModalOpen, setIsModalOpen] = useState(true);

		useEffect(cleanupLaunchParams, []);

		const isProvisionedUser =
			source === SOURCE_COMPONENT.NOTIFICATION &&
			context === SOURCE_CONTEXT.TWC_PROVISIONED_NOTIFICATION;
		const welcomeScreen = isProvisionedUser ? 'welcome-screen-twcg-animation' : undefined;
		// Provisioned-notification entry is always non-admin; isSiteAdmin isn't reliably forwarded.
		const isAdmin = !isProvisionedUser && isSiteAdmin;

		const handleClose = useCallback(() => {
			cleanupLaunchParams();
			setIsModalOpen(false);
		}, []);

		if (!cloudId || !isModalOpen) {
			return null;
		}

		if (FeatureGates.checkGate(TWC_HOME_ONBOARDING_EXPERIENCE_GATE)) {
			return (
				<Suspense fallback={null}>
					<LazyTwcOnboardingExperience
						cloudId={cloudId}
						currentEnv={currentEnv}
						isAdmin={isAdmin}
						isOpen
						locale={locale}
						onClose={handleClose}
						orgId={orgId}
						product="townsquare"
						sourceComponent={toSourceComponent(source)}
						sourceContext={toSourceContext(context)}
						xflowOptions={{
							initialScreen: welcomeScreen,
							onComplete: handleClose,
							testId: 'message-template-twc-xflow-modal',
							welcomeScreen,
						}}
					/>
				</Suspense>
			);
		}

		return (
			<TwcXflowModal
				cloudId={cloudId}
				locale={locale}
				onClose={handleClose}
				testId="message-template-twc-xflow-modal"
				sourceComponent={toSourceComponent(source)}
				sourceContext={toSourceContext(context)}
			/>
		);
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
